import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateMenusOptions,
  BulkCreateMenusResponse,
  BulkUpdateMenuOptions,
  BulkUpdateMenuResponse,
  DuplicateMenuOptions,
  DuplicateMenuResponse,
  GetMenuSiteUrlResponse,
  ListMenusOptions,
  ListMenusResponse,
  MaskedMenu,
  Menu,
  MenuCreatedEnvelope,
  MenuDeletedEnvelope,
  MenuQuery,
  MenuUpdatedEnvelope,
  MenusQueryBuilder,
  QueryMenusResponse,
  QueryMenusSiteUrlOptions,
  QueryMenusSiteUrlResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateMenu,
  bulkCreateMenus as universalBulkCreateMenus,
  bulkUpdateMenu as universalBulkUpdateMenu,
  createMenu as universalCreateMenu,
  deleteMenu as universalDeleteMenu,
  duplicateMenu as universalDuplicateMenu,
  getMenu as universalGetMenu,
  getMenuSiteUrl as universalGetMenuSiteUrl,
  listMenus as universalListMenus,
  queryMenus as universalQueryMenus,
  queryMenusSiteUrl as universalQueryMenusSiteUrl,
  typedQueryMenus as universalTypedQueryMenus,
  updateExtendedFields as universalUpdateExtendedFields,
  updateMenu as universalUpdateMenu,
} from './restaurants-menus-v1-menu-menus.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createMenu(httpClient: HttpClient): CreateMenuSignature {
  return (menu: Menu) =>
    universalCreateMenu(
      menu,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateMenuSignature {
  /**
   * > **Note:** The Menus API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates a menu.
   *
   * To create multiple menus at once, use [Bulk Create Menus](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/menu/bulk-create-menus).
   * @param - Menu details.
   * @returns Menu.
   */
  (menu: Menu): Promise<
    NonNullablePaths<
      Menu,
      | `name`
      | `sectionIds`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function bulkCreateMenus(
  httpClient: HttpClient
): BulkCreateMenusSignature {
  return (menus: Menu[], options?: BulkCreateMenusOptions) =>
    universalBulkCreateMenus(
      menus,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateMenusSignature {
  /**
   * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates multiple menus at once.
   * @param - Menu details.
   */
  (menus: Menu[], options?: BulkCreateMenusOptions): Promise<
    NonNullablePaths<
      BulkCreateMenusResponse,
      | `results`
      | `results.${number}.menuMetadata.originalIndex`
      | `results.${number}.menuMetadata.success`
      | `results.${number}.menuMetadata.error.code`
      | `results.${number}.menuMetadata.error.description`
      | `results.${number}.menu.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getMenu(httpClient: HttpClient): GetMenuSignature {
  return (menuId: string) =>
    universalGetMenu(
      menuId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMenuSignature {
  /**
   * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves a menu by ID.
   * @param - Menu ID.
   * @returns Menu.
   */
  (menuId: string): Promise<
    NonNullablePaths<
      Menu,
      | `name`
      | `sectionIds`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function listMenus(httpClient: HttpClient): ListMenusSignature {
  return (options?: ListMenusOptions) =>
    universalListMenus(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMenusSignature {
  /**
   * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves a list of up to 500 menus.
   */
  (options?: ListMenusOptions): Promise<
    NonNullablePaths<
      ListMenusResponse,
      | `menus`
      | `menus.${number}.name`
      | `menus.${number}.seoData.settings.preventAutoRedirect`,
      6
    >
  >;
}

export function queryMenus(httpClient: HttpClient): QueryMenusSignature {
  return () =>
    universalQueryMenus(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMenusSignature {
  /**
   * Creates a query to retrieve a list of menus.
   *
   * The `queryMenus()` function builds a query to retrieve a list of menus and returns a `MenusQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/menus/menus-query-builder/find) function.
   *
   * You can refine the query by chaining `MenusQueryBuilder` functions onto the query. `MenusQueryBuilder` functions enable you to filter, sort, and control the results that `queryMenus()` returns.
   *
   * `queryMenus()` runs with the following `MenusQueryBuilder` defaults, which you can override:
   *
   * * [`limit(100)`](/menus/menus-query-builder/limit)
   *
   * The following `MenusQueryBuilder` functions are supported for `queryMenus()`. For a full description of the menu object, see the object returned for the [`items`](/menus/menus-query-result/items) property in `MenusQueryResult`.
   */
  (): MenusQueryBuilder;
}

export function typedQueryMenus(
  httpClient: HttpClient
): TypedQueryMenusSignature {
  return (query: MenuQuery) =>
    universalTypedQueryMenus(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryMenusSignature {
  /** */
  (query: MenuQuery): Promise<
    NonNullablePaths<
      QueryMenusResponse,
      | `menus`
      | `menus.${number}.name`
      | `menus.${number}.seoData.settings.preventAutoRedirect`,
      6
    >
  >;
}

export function updateMenu(httpClient: HttpClient): UpdateMenuSignature {
  return (_id: string, menu: NonNullablePaths<UpdateMenu, `revision`, 2>) =>
    universalUpdateMenu(
      _id,
      menu,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMenuSignature {
  /**
   * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates a menu.
   *
   * To update multiple menus at once, use [Bulk Update Menu](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/menu/bulk-update-menu).
   *
   * Each time a menu is updated, its revision increments by 1. The existing revision must be included when updating a menu. This ensures you're working with the latest menu information, and it prevents unintended overwrites.
   * @param - Menu ID.
   * @returns Updated menu.
   */
  (_id: string, menu: NonNullablePaths<UpdateMenu, `revision`, 2>): Promise<
    NonNullablePaths<
      Menu,
      | `name`
      | `sectionIds`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function bulkUpdateMenu(
  httpClient: HttpClient
): BulkUpdateMenuSignature {
  return (
    menus: NonNullablePaths<MaskedMenu, `menu._id` | `menu.revision`, 3>[],
    options?: BulkUpdateMenuOptions
  ) =>
    universalBulkUpdateMenu(
      menus,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateMenuSignature {
  /**
   * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates multiple menus at once.
   *
   * Each time a menu is updated, its revision increments by 1. The existing revision must be included when updating the menu. This ensures you're working with the latest menu information, and it prevents unintended overwrites.
   * @param - Menus to update.
   */
  (
    menus: NonNullablePaths<MaskedMenu, `menu._id` | `menu.revision`, 3>[],
    options?: BulkUpdateMenuOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateMenuResponse,
      | `results`
      | `results.${number}.menuMetadata.originalIndex`
      | `results.${number}.menuMetadata.success`
      | `results.${number}.menuMetadata.error.code`
      | `results.${number}.menuMetadata.error.description`
      | `results.${number}.menu.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates only the `extendedFields` field.
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<NonNullablePaths<UpdateExtendedFieldsResponse, `namespace`, 2>>;
}

export function deleteMenu(httpClient: HttpClient): DeleteMenuSignature {
  return (menuId: string) =>
    universalDeleteMenu(
      menuId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMenuSignature {
  /**
   * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Deletes a menu.
   * @param - Menu ID.
   */
  (menuId: string): Promise<void>;
}

export function duplicateMenu(httpClient: HttpClient): DuplicateMenuSignature {
  return (_id: string, options?: DuplicateMenuOptions) =>
    universalDuplicateMenu(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DuplicateMenuSignature {
  /** @param - Menu id to be duplicated. */
  (_id: string, options?: DuplicateMenuOptions): Promise<
    NonNullablePaths<DuplicateMenuResponse, `menuIds`, 2>
  >;
}

export function getMenuSiteUrl(
  httpClient: HttpClient
): GetMenuSiteUrlSignature {
  return (_id: string) =>
    universalGetMenuSiteUrl(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMenuSiteUrlSignature {
  /**
   * Get menu details and path URL by menu ID.
   * @param - Menu ID.
   */
  (_id: string): Promise<
    NonNullablePaths<GetMenuSiteUrlResponse, `menuSiteUrl.path`, 3>
  >;
}

export function queryMenusSiteUrl(
  httpClient: HttpClient
): QueryMenusSiteUrlSignature {
  return (options?: QueryMenusSiteUrlOptions) =>
    universalQueryMenusSiteUrl(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMenusSiteUrlSignature {
  /**
   * Query menu details and path URL
   */
  (options?: QueryMenusSiteUrlOptions): Promise<
    NonNullablePaths<
      QueryMenusSiteUrlResponse,
      `menuSiteUrls` | `menuSiteUrls.${number}.path`,
      4
    >
  >;
}

export const onMenuCreated = EventDefinition(
  'wix.restaurants.menus.v1.menu_created',
  true,
  (event: MenuCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MenuCreatedEnvelope>();
export const onMenuDeleted = EventDefinition(
  'wix.restaurants.menus.v1.menu_deleted',
  true,
  (event: MenuDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MenuDeletedEnvelope>();
export const onMenuUpdated = EventDefinition(
  'wix.restaurants.menus.v1.menu_updated',
  true,
  (event: MenuUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.externalReferenceInfo.lastSyncDate' },
          ],
        },
      ])
    )
)<MenuUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateMenuResult,
  BulkCreateMenusOptions,
  BulkCreateMenusRequest,
  BulkCreateMenusResponse,
  BulkMenuResult,
  BulkUpdateMenuOptions,
  BulkUpdateMenuRequest,
  BulkUpdateMenuResponse,
  BusinessLocationDetails,
  CloneMenusRequest,
  CloneMenusResponse,
  CreateMenuRequest,
  CreateMenuResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DeleteMenuRequest,
  DeleteMenuResponse,
  DeleteOrphanSections,
  DomainEvent,
  DomainEventBodyOneOf,
  DuplicateMenuOptions,
  DuplicateMenuRequest,
  DuplicateMenuResponse,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  ExternalReferenceInfo,
  File,
  GetMenuRequest,
  GetMenuResponse,
  GetMenuSiteUrlRequest,
  GetMenuSiteUrlResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  Keyword,
  ListMenusOptions,
  ListMenusRequest,
  ListMenusResponse,
  MaskedMenu,
  Menu,
  MenuCreatedEnvelope,
  MenuDeletedEnvelope,
  MenuQuerySpec,
  MenuSiteUrl,
  MenuUpdatedEnvelope,
  MenusDataCloningCompleted,
  MenusQueryBuilder,
  MenusQueryResult,
  MessageEnvelope,
  Page,
  Pages,
  QueryMenusRequest,
  QueryMenusResponse,
  QueryMenusSiteUrlOptions,
  QueryMenusSiteUrlRequest,
  QueryMenusSiteUrlResponse,
  RestoreInfo,
  RunMenusMultiLocationMigrationRequest,
  RunMenusMultiLocationMigrationResponse,
  SeoSchema,
  Settings,
  SortOrder,
  Sorting,
  SyncContentToMultilingualRequest,
  SyncContentToMultilingualResponse,
  Tag,
  URI,
  URIs,
  UpdateBusinessLocationIdRequest,
  UpdateBusinessLocationIdResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateMenu,
  UpdateMenuRequest,
  UpdateMenuResponse,
  WebhookIdentityType,
  utils,
} from './restaurants-menus-v1-menu-menus.universal.js';
